import { Box, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as t } from '../../i18n/translations';
import { CandidateStatus } from '../../types/api';
import { T, TIER_RAIL, Tier } from '../../theme/tokens';
import WeightedChip, { useGenericWeights } from '../Summary/WeightedChip';
import { TableRow } from './CandidateTable';

// ── Constants ─────────────────────────────────────────────────────────────────

// Background tint for the highlighted block at the bottom of a candidate
// card (not the whole card — see the card render below) — a faded wash of
// TIER_RAIL's green/amber/red so the tier reads clearly without fighting
// with the text sitting on top of it.
const TIER_BLOCK_TINT: Record<Tier | 'unknown', string> = {
  top:     'rgba(46,158,91,0.16)',
  mid:     'rgba(201,154,30,0.16)',
  bottom:  'rgba(193,68,58,0.16)',
  unknown: 'rgba(169,161,140,0.14)',
};

type SortBy = 'score' | 'rank' | 'name' | 'team';

// ── Native select ─────────────────────────────────────────────────────────────

const NativeSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  flex?: number;
}> = ({ label, value, onChange, options, flex }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: flex ?? 1 }}>
    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textFaint }}>{label}</Typography>
    <Box
      component="select"
      value={value}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      sx={{
        height: 28, px: '8px', border: `1px solid ${T.borderInput}`,
        bgcolor: T.surface, color: T.text, fontSize: 11, cursor: 'pointer',
        outline: 'none', borderRadius: '2px', appearance: 'auto',
        '&:focus': { borderColor: T.ink },
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </Box>
  </Box>
);

// ── Tier segment ──────────────────────────────────────────────────────────────

const TierSegment: React.FC<{ value: Tier | 'all'; onChange: (v: Tier | 'all') => void; labels: string[] }> = ({ value, onChange, labels }) => {
  const opts: (Tier | 'all')[] = ['all', 'top', 'mid', 'bottom'];
  return (
    <Box sx={{ display: 'flex' }}>
      {opts.map((opt, i) => {
        const active = value === opt;
        return (
          <Box
            key={opt}
            component="button"
            onClick={() => onChange(opt)}
            sx={{
              height: 28, px: '8px',
              border: `1px solid ${T.borderInput}`,
              borderInlineEnd: i < opts.length - 1 ? 'none' : `1px solid ${T.borderInput}`,
              bgcolor: active ? T.ink : 'transparent',
              color: active ? T.bg : T.text,
              cursor: 'pointer', fontSize: 10, fontWeight: active ? 500 : 400,
              outline: 'none', transition: 'background-color 120ms ease',
              borderStartStartRadius: i === 0 ? '2px' : 0,
              borderEndStartRadius:   i === 0 ? '2px' : 0,
              borderStartEndRadius:   i === opts.length - 1 ? '2px' : 0,
              borderEndEndRadius:     i === opts.length - 1 ? '2px' : 0,
              '&:hover': { bgcolor: active ? T.text : T.surfaceAlt },
            }}
          >
            {labels[i]}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CandidateListPanelProps {
  rows: TableRow[];
  totalCount: number;
  selectedId: string | null;
  onSelect: (row: TableRow) => void;

  search: string;           onSearch: (v: string) => void;
  statusFilter: string;     onStatusFilter: (v: string) => void;
  teamFilter: string;       onTeamFilter: (v: string) => void;
  matamFilter: string;      onMatamFilter: (v: string) => void;
  tierFilter: Tier | 'all'; onTierFilter: (v: Tier | 'all') => void;
  evaluatorFilter: string;  onEvaluatorFilter: (v: string) => void;

  teamNumbers: number[];
  matamNumbers: number[];
  evaluatorOptions: string[];

  cycleId: string;
  cycleOptions: { value: string; label: string }[];
  onCycleChange: (id: string) => void;

  isFullscreen?: boolean;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// ── Main component ────────────────────────────────────────────────────────────

const CandidateListPanel: React.FC<CandidateListPanelProps> = ({
  rows, totalCount, selectedId, onSelect,
  search, onSearch, statusFilter, onStatusFilter,
  teamFilter, onTeamFilter, matamFilter, onMatamFilter, tierFilter, onTierFilter,
  evaluatorFilter, onEvaluatorFilter,
  teamNumbers, matamNumbers, evaluatorOptions,
  cycleId, cycleOptions, onCycleChange,
  isFullscreen = false,
  searchInputRef,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const { lang } = useDirection();
  const assessWeights = useGenericWeights(cycleId);
  const [sortBy, setSortBy]   = useState<SortBy>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [colorOn, setColorOn] = useState(false);

  const statusOptions = useMemo(() => [
    { value: 'all',                              label: t('filter.allStatuses', lang) },
    { value: CandidateStatus.ACTIVE,             label: t('status.active',   lang) },
    { value: CandidateStatus.INACTIVE,           label: t('status.withdrawn', lang) },
    { value: CandidateStatus.GIBUSH_COMPLETED,   label: t('status.completed', lang) },
    { value: CandidateStatus.GIBUSH_INCOMPLETED, label: t('status.incomplete', lang) },
  ], [lang]);

  const sortOptions = useMemo(() => [
    { value: 'score', label: t('sort.score', lang) },
    { value: 'rank',  label: t('sort.rank',  lang) },
    { value: 'name',  label: t('sort.name',  lang) },
    { value: 'team',  label: t('sort.team',  lang) },
  ], [lang]);

  const tierLabels = useMemo(() => [
    t('tier.all', lang), t('tier.top', lang), t('tier.mid', lang), t('tier.bottom', lang),
  ], [lang]);

  const statusLabel = (s: CandidateStatus): string => {
    switch (s) {
      case CandidateStatus.ACTIVE:             return t('status.active',    lang);
      case CandidateStatus.INACTIVE:           return t('status.withdrawn', lang);
      case CandidateStatus.GIBUSH_COMPLETED:   return t('status.completed', lang);
      case CandidateStatus.GIBUSH_INCOMPLETED: return t('status.incomplete', lang);
      default:                                 return s;
    }
  };

  const statusDot = (s: CandidateStatus): string => {
    switch (s) {
      case CandidateStatus.ACTIVE:           return T.tierTop;
      case CandidateStatus.INACTIVE:         return T.danger;
      case CandidateStatus.GIBUSH_COMPLETED: return T.ink;
      default:                               return T.tierMid;
    }
  };

  const teamOptions = useMemo(() => [
    { value: 'all', label: t('filter.allTeams', lang) },
    ...teamNumbers.map(n => ({ value: String(n), label: `${t('filter.team', lang)} ${n}` })),
  ], [teamNumbers, lang]);

  const matamOptions = useMemo(() => [
    { value: 'all', label: t('filter.allMatams', lang) },
    { value: 'unknown', label: t('filter.matamUnknown', lang) },
    ...matamNumbers.map(n => ({ value: String(n), label: `${t('filter.matam', lang)} ${n}` })),
  ], [matamNumbers, lang]);

  const evalOpts = useMemo(() => [
    { value: 'all', label: t('filter.allEvaluators', lang) },
    ...evaluatorOptions.map(n => ({ value: n, label: n })),
  ], [evaluatorOptions, lang]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      switch (sortBy) {
        case 'score': av = Number(a.finalCycleScore ?? -1);          bv = Number(b.finalCycleScore ?? -1);          break;
        case 'rank':  av = Number(a._rank ?? 9999);                   bv = Number(b._rank ?? 9999);                   break;
        case 'name':  av = (a._displayName ?? '').toLowerCase();      bv = (b._displayName ?? '').toLowerCase();      break;
        case 'team':  av = Number(a.teamNumber);                      bv = Number(b.teamNumber);                      break;
        default:      return 0;
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === 'asc' ? av - (bv as number) : (bv as number) - av;
    });
  }, [rows, sortBy, sortDir]);

  if (isCollapsed) {
    return (
      <Box sx={{ width: 48, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: T.surface, pt: '8px', gap: '10px' }}>
        <Box
          component="button"
          onClick={onToggleCollapse}
          title={t('cc.expandSidebar', lang)}
          sx={{
            width: 32, height: 32, border: `1px solid ${T.borderInput}`, bgcolor: 'transparent',
            color: T.textMuted, cursor: 'pointer', borderRadius: '2px', fontSize: 13, outline: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { bgcolor: T.surfaceAlt },
          }}
        >▶</Box>
        <Box sx={{ px: '6px', py: '2px', bgcolor: T.borderSoft, borderRadius: '2px' }}>
          <Typography sx={{ fontSize: 10, color: T.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{rows.length}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: T.surface }}>

      {/* ── Fixed header ── */}
      <Box sx={{ px: '12px', pt: '12px', pb: '10px', borderBottom: `1px solid ${T.borderSoft}`, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>

        {/* Title + count + collapse toggle */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink, letterSpacing: '0.02em' }}>
            {t('list.candidates', lang)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography sx={{ fontSize: 11, color: T.textMuted, fontVariantNumeric: 'tabular-nums' }}>
              {rows.length}<span style={{ color: T.textFaint }}> / </span>{totalCount}
            </Typography>
            {onToggleCollapse && (
              <Box
                component="button"
                onClick={onToggleCollapse}
                title={t('cc.collapseSidebar', lang)}
                sx={{
                  width: 20, height: 20, border: `1px solid ${T.borderInput}`, bgcolor: 'transparent',
                  color: T.textMuted, cursor: 'pointer', borderRadius: '2px', fontSize: 10, outline: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  '&:hover': { bgcolor: T.surfaceAlt },
                }}
              >◀</Box>
            )}
          </Box>
        </Box>

        {/* Cycle selector */}
        <Box
          component="select"
          value={cycleId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onCycleChange(e.target.value)}
          sx={{
            height: 28, px: '8px', width: '100%',
            border: `1px solid ${T.borderInput}`, bgcolor: T.surface, color: T.text,
            fontSize: 11, outline: 'none', borderRadius: '2px', appearance: 'auto',
            '&:focus': { borderColor: T.ink },
          }}
        >
          <option value="">{t('list.selectCycle', lang)}</option>
          {cycleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Box>

        {/* Search */}
        <Box
          component="input"
          ref={searchInputRef}
          type="text"
          placeholder={t('list.searchPlaceholder', lang)}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
          sx={{
            height: 28, px: '8px', width: '100%', boxSizing: 'border-box',
            border: `1px solid ${T.borderInput}`, bgcolor: T.surface, color: T.text,
            fontSize: 11, outline: 'none', borderRadius: '2px',
            '&::placeholder': { color: T.textFaint },
            '&:focus': { borderColor: T.ink },
          }}
        />

        {/* Filter grid — 3 columns so Matam sits right next to Team */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
          <NativeSelect label={t('filter.status', lang)} value={statusFilter} onChange={onStatusFilter} options={statusOptions} />
          <NativeSelect label={t('filter.team',   lang)} value={teamFilter}   onChange={onTeamFilter}   options={teamOptions} />
          <NativeSelect label={t('filter.matam',  lang)} value={matamFilter}  onChange={onMatamFilter}  options={matamOptions} />
          <NativeSelect label={t('filter.sort',   lang)} value={sortBy}       onChange={v => setSortBy(v as SortBy)} options={sortOptions} />
          {/* Sort direction toggle */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textMuted, fontWeight: 600 }}>
              {t('filter.direction', lang)}
            </Typography>
            <Box
              component="button"
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              sx={{
                height: 28, px: '8px',
                border: `1px solid ${T.borderInput}`, bgcolor: 'transparent',
                color: T.text, cursor: 'pointer', fontSize: 11,
                outline: 'none', borderRadius: '2px', textAlign: 'start',
                display: 'flex', alignItems: 'center', gap: '4px',
                '&:hover': { bgcolor: T.surfaceAlt },
              }}
            >
              <Typography component="span" sx={{ fontSize: 10 }}>{sortDir === 'asc' ? '▲' : '▼'}</Typography>
              <Typography component="span" sx={{ fontSize: 11 }}>
                {sortDir === 'asc' ? t('filter.ascending', lang) : t('filter.descending', lang)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tier + evaluator row */}
        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textMuted, fontWeight: 600 }}>
              {t('filter.tier', lang)}
            </Typography>
            <TierSegment value={tierFilter} onChange={onTierFilter} labels={tierLabels} />
          </Box>
          {evaluatorOptions.length > 0 && (
            <NativeSelect label={t('filter.evaluator', lang)} value={evaluatorFilter} onChange={onEvaluatorFilter} options={evalOpts} />
          )}
        </Box>

        {/* Color toggle + legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box
              component="button"
              onClick={() => setColorOn(v => !v)}
              sx={{
                height: 22, px: '8px', fontSize: 10, fontWeight: colorOn ? 500 : 400,
                border: `1px solid ${T.borderInput}`, bgcolor: colorOn ? T.surfaceAlt : 'transparent',
                color: T.textMuted, cursor: 'pointer', borderRadius: '2px', outline: 'none',
                '&:hover': { bgcolor: T.surfaceAlt },
              }}
            >
              {colorOn ? t('color.on', lang) : t('color.off', lang)}
            </Box>
            {colorOn && (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {(['top', 'mid', 'bottom'] as Tier[]).map(tier => (
                  <Box key={tier} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_RAIL[tier] }} />
                    <Typography sx={{ fontSize: 10, color: T.textMuted }}>
                      {t(`tier.${tier}` as const, lang)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: T.textFaint }} />
                  <Typography sx={{ fontSize: 10, color: T.textFaint }}>{t('tier.unknown', lang)}</Typography>
                </Box>
              </Box>
            )}
          </Box>
      </Box>

      {/* ── Scrollable card list ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '8px', py: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {sorted.length === 0 ? (
          <Box sx={{ py: '48px', textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: T.textFaint }}>{t('list.noResults', lang)}</Typography>
          </Box>
        ) : sorted.map(row => {
          const isSelected = selectedId === row.id;
          const tierKey    = row._tier ?? 'unknown';
          const tierColor  = row._tier ? TIER_RAIL[row._tier] : T.textFaint;

          return (
            <Box
              key={row.id}
              onClick={() => onSelect(row)}
              sx={{
                position: 'relative',
                px: '12px',
                pt: '10px',
                border: `1px solid ${isSelected ? T.ink : T.borderSoft}`,
                borderInlineStart: `3px solid ${isSelected ? T.ink : (colorOn ? tierColor : T.borderSoft)}`,
                bgcolor: isSelected ? T.surfaceSel : T.surface,
                cursor: 'pointer',
                transition: 'background-color 100ms ease',
                overflow: 'hidden',
                // The parent list is a flex column (line 359) — overflow other
                // than 'visible' resets a flex item's automatic min-height from
                // "content size" to 0, so without this the browser was free to
                // crush every row toward 0px once there were enough candidates
                // that they didn't all fit (no error, just flex layout math).
                flexShrink: 0,
                '&:hover': { bgcolor: isSelected ? T.surfaceSel : '#FDFAF1' },
              }}
            >
              {/* Tier dot */}
              {colorOn && (
                <Box sx={{ position: 'absolute', top: 8, insetInlineStart: 5, width: 5, height: 5, borderRadius: '50%', bgcolor: tierColor }} />
              )}

              {/* Row 1: name + score */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row._namesLoading && !row._displayName
                    ? `#${row.candidateNumber}`
                    : row._displayName ?? `#${row.candidateNumber}`
                  }
                </Typography>
                {row.finalCycleScore != null && (
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {Number(row.finalCycleScore).toFixed(1)}
                  </Typography>
                )}
              </Box>

              {/* Highlighted block — everything below the score, tinted with
                  the tier color at low opacity (not the whole card) so the
                  color reads clearly without fighting the text sitting on
                  top of it. Bleeds edge-to-edge via negative margins matching
                  the card's own padding. */}
              <Box
                sx={{
                  mx: '-12px',
                  mt: '6px',
                  px: '12px',
                  pt: '5px',
                  pb: '10px',
                  bgcolor: colorOn ? TIER_BLOCK_TINT[tierKey] : 'transparent',
                }}
              >
                {/* Row 2: identifiers */}
                <Typography sx={{ fontSize: 11, color: T.textMuted }}>
                  {t('drawer.candidateNumber', lang)} {row.candidateNumber} · {t('filter.team', lang)} {row.teamNumber}
                  {row._candidateRankInTeam && (
                    row._candidateRankInTeam === 'unknown'
                      ? ` · ${t('drawer.rankUnknown', lang)}`
                      : ` · ${t('sort.rank', lang)} ${row._candidateRankInTeam}`
                  )}
                  {row._tier && (
                    <>
                      {' · '}
                      <Box component="span" sx={{ color: TIER_RAIL[row._tier], fontWeight: 700 }}>
                        {t(`tier.${row._tier}`, lang)}
                      </Box>
                    </>
                  )}
                </Typography>

                {/* Row 3: status badge + red flag */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '5px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: statusDot(row.status), flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>{statusLabel(row.status)}</Typography>
                  </Box>
                  {(row._redFlag || row.redFlag) && (
                    <Typography sx={{ fontSize: 10, color: T.danger, fontWeight: 500 }}>⚑ {t('drawer.redFlag', lang)}</Typography>
                  )}
                </Box>

                {/* Row 4 (fullscreen only): mini score breakdown */}
                {isFullscreen && (
                  <Box sx={{ display: 'flex', gap: '8px', mt: '6px', pt: '6px', borderTop: `1px solid ${T.borderSoft}` }}>
                    {[
                      { key: 'S', value: (row as any).finalSocioScore,      color: T.tierTop },
                      { key: 'A', value: (row as any).finalAssesmentScore,   color: T.tierMid },
                      { key: 'I', value: (row as any).finalInterviewScore,   color: T.textSubtle },
                    ].map(({ key, value, color }) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                        <Typography sx={{ fontSize: 9, color: T.textSubtle, textTransform: 'uppercase', fontWeight: 600 }}>{key}</Typography>
                        {key === 'A' && assessWeights && <WeightedChip weights={assessWeights} />}
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: value != null ? color : T.textFaint, fontVariantNumeric: 'tabular-nums' }}>
                          {value != null ? Number(value).toFixed(1) : '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CandidateListPanel;
